import { BuildingOffice2Icon, ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchCitiesByCountry } from "../../services/api";

interface CityDropdownProps {
  country: string;
  value: string;
  onCityChange: (value: string) => void;
}

export const CityDropdown: React.FC<CityDropdownProps> = ({
  country,
  value,
  onCityChange,
}) => {
  const { t } = useTranslation("events");
  const [cities, setCities] = useState<string[]>([]);
  const [searchText, setSearchText] = useState(value || "");
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const previousCountry = useRef("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearchText(value || "");
  }, [value]);

  useEffect(() => {
    if (!country) {
      setCities([]);
      setSearchText("");
      onCityChange("");
      previousCountry.current = "";
      return;
    }

    if (country === previousCountry.current) return;

    if (previousCountry.current) {
      setSearchText("");
      onCityChange("");
    }

    previousCountry.current = country;
    setIsLoading(true);
    setCities([]);

    fetchCitiesByCountry(country)
      .then(setCities)
      .catch((err) => console.error("Failed to load cities", err))
      .finally(() => setIsLoading(false));
  }, [country, onCityChange]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchText(value || "");
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [value]);

  const isDisabled = !country || isLoading;
  const filteredCities = searchText.trim()
    ? cities.filter((city) =>
        city.toLowerCase().includes(searchText.toLowerCase())
      )
    : cities;

  const handleSelectCity = (event: React.MouseEvent, city: string) => {
    event.preventDefault();
    setSearchText(city);
    onCityChange(city);
    setIsOpen(false);
  };

  const handleClearCity = (event: React.MouseEvent) => {
    event.preventDefault();
    setSearchText("");
    onCityChange("");
    setIsOpen(true);
  };

  return (
    <div ref={containerRef} className="relative flex-1 min-w-0 w-full">
      <div
        className={`flex items-center gap-2 px-2 h-8 rounded-lg border bg-white min-w-0 ${
          isDisabled
            ? "border-gray-200 bg-gray-50 opacity-70"
            : isOpen
            ? "border-blue-500 ring-2 ring-blue-100"
            : "border-gray-300"
        }`}
      >
        <BuildingOffice2Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />

        <input
          type="text"
          value={isLoading ? "" : searchText}
          placeholder={
            !country
              ? t("select_country_first", "Select country first")
              : isLoading
              ? t("loading_cities", "Loading cities...")
              : t("search_city", "Search city...")
          }
          onChange={(event) => {
            setSearchText(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (!isDisabled) setIsOpen(true);
          }}
          disabled={isDisabled}
          className="flex-1 min-w-0 truncate text-sm bg-transparent outline-none placeholder-gray-400 disabled:cursor-not-allowed"
        />

        {value && !isDisabled && (
          <button
            type="button"
            onMouseDown={handleClearCity}
            className="text-gray-300 hover:text-gray-500 flex-shrink-0"
            aria-label={t("clear_city", "Clear city")}
          >
            <XMarkIcon className="w-3.5 h-3.5" />
          </button>
        )}

        <ChevronDownIcon
          className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      {isOpen && !isDisabled && (
        <ul className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1 text-sm">
          {filteredCities.length > 0 ? (
            filteredCities.map((city) => (
              <li
                key={city}
                onMouseDown={(event) => handleSelectCity(event, city)}
                className={`px-3 py-1.5 cursor-pointer rounded-xl hover:bg-[#EBEBEC] text-gray-900 ${
                  city === value
                    ? "bg-[#EBEBEC] text-gray-900"
                    : "text-gray-900"
                }`}
              >
                {city}
              </li>
            ))
          ) : (
            <li className="px-3 py-2 text-gray-400">
              {t("no_cities_found", "No cities found")}
            </li>
          )}
        </ul>
      )}
    </div>
  );
};
